import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { getDashboardStats, getUpcomingBirthdays } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Users, 
  Calendar, 
  Package, 
  Megaphone, 
  Wallet,
  ArrowRight,
  Clock,
  TrendingUp,
  TrendingDown,
  UserCheck,
  Cake,
  BarChart3,
  PieChart
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartPie, Pie, Cell } from 'recharts';

export default function Dashboard() {
  const { user, role } = useAuth();
  const { formatCurrency: ctxFormatCurrency } = useNotifications();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, birthdaysRes] = await Promise.all([
        getDashboardStats(),
        getUpcomingBirthdays(14)
      ]);
      setStats(statsRes.data);
      setUpcomingBirthdays(birthdaysRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d 'de' MMMM", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    try { return ctxFormatCurrency(amount); }
    catch { return `€${(amount || 0).toFixed(2)}`; }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      alta: 'bg-red-100 text-red-700 border-red-200',
      media: 'bg-amber-100 text-amber-700 border-amber-200',
      normal: 'bg-emerald-100 text-emerald-700 border-emerald-200'
    };
    return styles[priority] || styles.normal;
  };

  const getEventTypeBadge = (type) => {
    const styles = {
      evento: 'bg-sky-100 text-sky-700',
      ensayo: 'bg-emerald-100 text-emerald-700',
      reunion: 'bg-amber-100 text-amber-700'
    };
    return styles[type] || 'bg-stone-100 text-stone-700';
  };

  // Prepare chart data
  const financeChartData = stats?.kpis?.monthly_income ? 
    Object.keys(stats.kpis.monthly_income).map(month => ({
      month: month.slice(5),
      ingresos: stats.kpis.monthly_income[month] || 0,
      gastos: stats.kpis.monthly_expense[month] || 0
    })).slice(-6) : [];

  const attendanceByTypeData = stats?.kpis?.event_type_stats ?
    Object.entries(stats.kpis.event_type_stats).map(([type, data]) => ({
      name: type.charAt(0).toUpperCase() + type.slice(1),
      value: data.rate,
      total: data.total
    })) : [];

  const COLORS = ['#059669', '#0284C7', '#F59E0B', '#8B5CF6'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Hero Section */}
      <div className="hero-gradient rounded-2xl p-8 text-white relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1614183907279-4c5c1640ed77?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10">
          <h1 className="text-3xl lg:text-4xl font-bold font-['Outfit'] mb-2">
            ¡Bienvenido, {user?.full_name?.split(' ')[0]}!
          </h1>
          <p className="text-emerald-100 text-lg mb-4">
            {role?.name || 'Miembro'} de PICHIROCES AMAZONICOS
          </p>
          <div className="flex items-center gap-2 text-emerald-200 text-sm">
            <Clock className="w-4 h-4" />
            <span>{format(new Date(), "EEEE, d 'de' MMMM yyyy", { locale: es })}</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card card-hover cursor-pointer" onClick={() => navigate('/erp/users')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Miembros</p>
                <p className="text-3xl font-bold text-emerald-900 font-['Outfit']">
                  {stats?.users_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover cursor-pointer" onClick={() => navigate('/erp/calendar')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Eventos</p>
                <p className="text-3xl font-bold text-sky-700 font-['Outfit']">
                  {stats?.events_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-sky-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover cursor-pointer" onClick={() => navigate('/erp/inventory')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Inventario</p>
                <p className="text-3xl font-bold text-amber-600 font-['Outfit']">
                  {stats?.inventory_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Package className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover cursor-pointer" onClick={() => navigate('/erp/finance')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Balance</p>
                <p className={`text-2xl font-bold font-['Outfit'] ${stats?.balance >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  {formatCurrency(stats?.balance || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-emerald-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPIs Section */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Finance KPIs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Resumen Financiero
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/erp/finance')}
              className="text-emerald-700"
            >
              Ver más <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-emerald-50 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">Ingresos</p>
                <p className="font-bold text-emerald-600">{formatCurrency(stats?.total_income || 0)}</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <TrendingDown className="w-5 h-5 text-red-600 mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">Gastos</p>
                <p className="font-bold text-red-600">{formatCurrency(stats?.total_expense || 0)}</p>
              </div>
              <div className="text-center p-3 bg-sky-50 rounded-lg">
                <Users className="w-5 h-5 text-sky-600 mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">Aportaciones</p>
                <p className="font-bold text-sky-600">{formatCurrency(stats?.total_contribution || 0)}</p>
              </div>
            </div>
            
            {financeChartData.length > 0 && (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={financeChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="ingresos" fill="#059669" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="gastos" fill="#dc2626" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Attendance KPIs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              Participación
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/erp/attendance')}
              className="text-emerald-700"
            >
              Ver más <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-muted-foreground">Tasa de asistencia general</span>
                <span className="font-bold text-emerald-700">
                  {(stats?.kpis?.overall_attendance_rate || 0).toFixed(1)}%
                </span>
              </div>
              <Progress value={stats?.kpis?.overall_attendance_rate || 0} className="h-3" />
            </div>

            {attendanceByTypeData.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartPie>
                      <Pie
                        data={attendanceByTypeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={25}
                        outerRadius={45}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {attendanceByTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                    </RechartPie>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-col justify-center space-y-2">
                  {attendanceByTypeData.map((item, index) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm">{item.name}</span>
                      <span className="text-sm font-medium ml-auto">{item.value.toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Participants */}
            {stats?.kpis?.top_participants?.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Mejores asistencias</p>
                <div className="space-y-2">
                  {stats.kpis.top_participants.slice(0, 3).map((p, idx) => (
                    <div key={p.user_id} className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg">
                      <span className="text-sm font-bold text-muted-foreground w-4">
                        #{idx + 1}
                      </span>
                      <Avatar className="w-6 h-6">
                        <AvatarImage src={p.avatar_url} />
                        <AvatarFallback className="text-xs bg-emerald-100 text-emerald-700">
                          {getInitials(p.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm flex-1 truncate">{p.full_name}</span>
                      <Badge variant="outline" className="text-xs">
                        {p.attendance_rate.toFixed(0)}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upcoming Events */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-['Outfit']">Próximos Eventos</CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/erp/calendar')}
              className="text-emerald-700"
            >
              Ver todos <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.upcoming_events?.length > 0 ? (
                stats.upcoming_events.map((event, idx) => (
                  <div 
                    key={event.id || idx}
                    className={`p-4 rounded-lg bg-stone-50 border-l-4 ${
                      event.event_type === 'evento' ? 'border-sky-500' :
                      event.event_type === 'ensayo' ? 'border-emerald-500' : 'border-amber-500'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-stone-900">{event.title}</h4>
                        <p className="text-sm text-stone-500 mt-1">
                          {formatDate(event.start_date)}
                          {event.location && ` • ${event.location}`}
                        </p>
                      </div>
                      <Badge className={getEventTypeBadge(event.event_type)}>
                        {event.event_type}
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-stone-500 py-8">
                  No hay eventos próximos
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Birthdays */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
              <Cake className="w-5 h-5 text-pink-500" />
              Próximos Cumpleaños
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingBirthdays.length > 0 ? (
                upcomingBirthdays.slice(0, 5).map((user) => (
                  <div 
                    key={user.id}
                    className="flex items-center gap-3 p-3 bg-gradient-to-r from-pink-50 to-amber-50 rounded-lg"
                  >
                    <Avatar className="w-10 h-10 border-2 border-pink-200">
                      <AvatarImage src={user.avatar_url} />
                      <AvatarFallback className="bg-pink-100 text-pink-700">
                        {getInitials(user.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{user.full_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {user.days_until_birthday === 0 
                          ? '¡Hoy!' 
                          : `En ${user.days_until_birthday} días`
                        }
                      </p>
                    </div>
                    {user.days_until_birthday === 0 && (
                      <span className="text-xl">🎂</span>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  No hay cumpleaños próximos
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-['Outfit']">Acciones Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/erp/messages')}
              data-testid="quick-messages"
            >
              <Megaphone className="w-5 h-5" />
              <span>Mensajes</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/erp/calendar')}
              data-testid="quick-calendar"
            >
              <Calendar className="w-5 h-5" />
              <span>Calendario</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/erp/attendance')}
              data-testid="quick-attendance"
            >
              <UserCheck className="w-5 h-5" />
              <span>Asistencia</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/erp/inventory')}
              data-testid="quick-inventory"
            >
              <Package className="w-5 h-5" />
              <span>Inventario</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/erp/finance')}
              data-testid="quick-finance"
            >
              <TrendingUp className="w-5 h-5" />
              <span>Finanzas</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
